import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Tooltip,
  Typography,
} from "antd";
import { LeftOutlined } from "@ant-design/icons";
import moment from "moment";
import { platformApi } from "../../../api";
import { ContextAPI } from "../../../ContextAPI";
import LabourTable from "./LabourTable";
import "./index.less";

const { Title } = Typography;
const { Option } = Select;

function LabourPerformFormInner({ form }) {
  const history = useHistory();
  const location = useLocation();
  const { loginCredintials } = useContext(ContextAPI);
  const { getFieldDecorator, setFieldsValue, getFieldValue, validateFields } =
    form;

  const url = location.pathname;
  const id = url.substring(url.lastIndexOf("/") + 1);
  const isNew = id === ":id";

  const [jobOptions, setJobOptions] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [labourRows, setLabourRows] = useState([]);
  const [saving, setSaving] = useState(false);
  // editable=true means can edit (create, or modify). List "Modify" passes editMode.
  const [editable, setEditable] = useState(
    isNew || Boolean(location.state?.editMode)
  );
  const [jobBranchId, setJobBranchId] = useState("");
  // VehicleMaster id for Job Code vehicle-specific charge (same as Job Invoice)
  const [vehicleMasterId, setVehicleMasterId] = useState(null);
  // When editing an existing record (or upserted from job), track id for PUT
  const [recordId, setRecordId] = useState(isNew ? null : id);

  const createAccess =
    loginCredintials?.roleAccess?.some(
      (each) => each.subModule === "labour_perform" && each.access?.create
    ) ?? false;
  const modifyAccess =
    loginCredintials?.roleAccess?.some(
      (each) => each.subModule === "labour_perform" && each.access?.update
    ) ?? false;

  const fallbackBranchId =
    loginCredintials?.branch?.[0]?.id ||
    loginCredintials?.employeeBranch?.[0]?.id ||
    "";

  const searchJobNo = (value) => {
    platformApi
      .post("/api/options/get/", {
        page: 1,
        size: 50,
        searchString: value || "",
        column: "jobNo",
        module: "jobOrders",
        except: [
          "Gate Pass",
          "Payment Received",
          "Invoice",
          "Proforma Invoice",
          "PAID",
        ],
      })
      .then((res) => {
        if (res.data?.response) setJobOptions(res.data.response);
      })
      .catch(() => message.error("Failed to fetch Job Order"));
  };

  const searchMechanic = (searchString) => {
    platformApi
      .post("/api/options/get/", {
        module: "user",
        column: "employeeName",
        searchString: searchString || "",
        searchColumns: ["employeeName"],
        size: 100,
        page: 1,
        except: null,
      })
      .then((result) => {
        if (result.data?.code === 200) {
          const mechs = (result.data.response || []).filter(
            (item) =>
              item.profile &&
              item.profile.department?.role === "Mechanic" &&
              item.status === true
          );
          setMechanics(mechs);
        }
      })
      .catch(() => {});
  };

  const fillJobFields = (job) => {
    if (!job) return;
    const str = `${job.customer?.name || ""} - ${job.customerPhone || ""}`;
    setFieldsValue({
      registerNo: job.vehicle?.registerNo || "",
      engineNo: job.vehicle?.engineNo || "",
      chassisNo: job.vehicle?.chassisNo || "",
      model: job.vehicle?.vehicle?.modelName || "",
      customer: str,
      mechanic: job.mechanic?.id || job.mechanic || undefined,
      branch: job.branch?.name || "",
    });
    setJobBranchId(job.branch?.id || "");
    setVehicleMasterId(job.vehicle?.vehicle?.id || null);
    if (job.mechanic) {
      const mechanicId = job.mechanic.id || job.mechanic;
      const mechanicObj =
        typeof job.mechanic === "object"
          ? job.mechanic
          : { id: mechanicId };
      setMechanics((prev) =>
        prev.some((m) => m.id === mechanicId)
          ? prev
          : [...prev, mechanicObj]
      );
    }
  };

  const applyExistingRecord = (mi, { editMode = false } = {}) => {
    if (!mi?.id) return;
    setRecordId(mi.id);
    if (mi.job?.id) {
      setJobOptions((prev) =>
        prev.some((j) => j.id === mi.job.id) ? prev : [...prev, mi.job]
      );
      const str = `${mi.job.customer?.name || ""} - ${
        mi.job.customerPhone || ""
      }`;
      setFieldsValue({
        JobNo: mi.job.id,
        registerNo: mi.job.vehicle?.registerNo || "",
        engineNo: mi.job.vehicle?.engineNo || "",
        chassisNo: mi.job.vehicle?.chassisNo || "",
        model: mi.job.vehicle?.vehicle?.modelName || "",
        customer: str,
        mechanic: mi.mechanic?.id,
        branch: mi.branch?.name || "",
      });
      if (mi.branch?.id) setJobBranchId(mi.branch.id);
      setVehicleMasterId(mi.job.vehicle?.vehicle?.id || null);
      if (mi.mechanic) {
        setMechanics((prev) =>
          prev.some((m) => m.id === mi.mechanic.id)
            ? prev
            : [...prev, mi.mechanic]
        );
      }
      platformApi.get("/api/jobOrder/" + mi.job.id).then((jobRes) => {
        const job = jobRes?.data?.response?.data;
        if (job) {
          setJobOptions((prev) =>
            prev.some((j) => j.id === job.id) ? prev : [...prev, job]
          );
          fillJobFields(job);
          if (mi.mechanic?.id) {
            setFieldsValue({ mechanic: mi.mechanic.id });
          }
        }
      });
    }
    setLabourRows(
      (mi.labour || []).map((l) => ({
        id: l.id,
        jobCode: l.jobCode,
        issueType: l.issueType || "PAID",
        issueDate: l.issueDate || moment().toISOString(),
        mrp: l.mrp || 0,
      }))
    );
    setEditable(editMode);
  };

  /** If this job already has labour_perform, open that record instead of a blank create. */
  const openExistingForJob = (jobId, { replaceUrl = true, editMode = false } = {}) => {
    if (!jobId) return Promise.resolve(false);
    return platformApi
      .get(`/api/jobOrder/${jobId}`)
      .then((res) => {
        const job = res?.data?.response?.data;
        const labourId =
          job?.labourPerformId ||
          (Array.isArray(job?.MaterialIssue)
            ? [...job.MaterialIssue]
                .filter((m) => m && m.type === "labour_perform")
                .pop()?.id
            : null);
        if (!labourId) return false;
        const goEdit = editMode || Boolean(location.state?.editMode);
        if (replaceUrl && (isNew || id !== labourId)) {
          history.replace({
            pathname: `/autoadmin/labour_perform/${labourId}`,
            state: {
              ...(location.state || {}),
              editMode: goEdit,
            },
          });
        }
        return platformApi.get(`/api/materialIssue/${labourId}`).then((miRes) => {
          const mi = miRes?.data?.response?.data;
          if (!mi) return false;
          applyExistingRecord(mi, { editMode: goEdit });
          return true;
        });
      })
      .catch(() => false);
  };

  const fetchJobDetails = (value) => {
    if (!value) {
      setVehicleMasterId(null);
      return;
    }
    setFieldsValue({ JobNo: value });
    platformApi.get("/api/jobOrder/" + value).then((res) => {
      const job = res?.data?.response?.data;
      if (!job) return;
      setJobOptions((prev) =>
        prev.some((j) => j.id === job.id) ? prev : [...prev, job]
      );
      fillJobFields(job);
      // Selecting a job that already has labour → load that record
      if (isNew || !recordId) {
        openExistingForJob(value, { replaceUrl: true, editMode: true });
      }
    });
  };

  useEffect(() => {
    searchJobNo("");
    searchMechanic("");

    if (!isNew) {
      platformApi.get(`/api/materialIssue/${id}`).then((res) => {
        const mi = res?.data?.response?.data;
        if (!mi) return;
        applyExistingRecord(mi, {
          editMode: Boolean(location.state?.editMode),
        });
      });
      return;
    }

    // New form: if navigated with jobNo, prefer existing labour for that job
    if (location.state?.jobNo) {
      setFieldsValue({ JobNo: location.state.jobNo });
      openExistingForJob(location.state.jobNo, {
        replaceUrl: true,
        editMode: Boolean(location.state?.editMode) || true,
      }).then((found) => {
        if (!found) {
          fetchJobDetails(location.state.jobNo);
          if (!labourRows.length) {
            setLabourRows([
              {
                id: `tmp-${Date.now()}`,
                jobCode: null,
                issueType: "PAID",
                issueDate: moment().toISOString(),
                mrp: 0,
              },
            ]);
          }
        }
      });
    } else if (!labourRows.length) {
      setLabourRows([
        {
          id: `tmp-${Date.now()}`,
          jobCode: null,
          issueType: "PAID",
          issueDate: moment().toISOString(),
          mrp: 0,
        },
      ]);
    }
  }, []);

  const netAmount = labourRows.reduce(
    (sum, r) => sum + (parseFloat(r.mrp) || 0),
    0
  );

  const handleSave = () => {
    validateFields((err, values) => {
      if (err) return;
      const validLines = labourRows.filter((r) => r.jobCode?.id);
      if (!validLines.length) {
        message.error("Add at least one labour/consumable line");
        return;
      }
      const submittedBranch = jobBranchId || fallbackBranchId;
      if (!submittedBranch) {
        message.error(
          "Please select a Job Order first. Branch cannot be determined."
        );
        return;
      }
      setSaving(true);
      const payload = {
        type: "labour_perform",
        jobOrder: values.JobNo,
        mechanic: values.mechanic,
        currentDate: moment().toISOString(),
        branch: submittedBranch,
        netAmount,
        labourItemInvoice: validLines.map((r) => ({
          jobCode: { id: r.jobCode.id },
          issueType: r.issueType || "PAID",
          issueDate: r.issueDate,
          mrp: r.mrp,
          mechanic: values.mechanic ? { id: values.mechanic } : null,
        })),
      };
      const saveReq = recordId
        ? platformApi.put(`/api/materialIssue/${recordId}`, payload)
        : platformApi.post("/api/materialIssue/", payload);
      saveReq
        .then((res) => {
          setSaving(false);
          if (res?.data?.code === 200 && res.data.response?.code === 200) {
            message.success(
              recordId ? "Labour Perform updated" : "Labour Perform saved"
            );
            history.push("/autoadmin/labour_perform");
          } else {
            message.error(
              res?.data?.response?.message ||
                res?.data?.message ||
                "Failed to save"
            );
          }
        })
        .catch((e) => {
          setSaving(false);
          message.error(e?.response?.data?.message || "Failed to save");
        });
    });
  };

  // editable=true means can edit; fieldsLocked when viewing
  const fieldsLocked = !editable;
  const isExisting = Boolean(recordId);

  return (
    <div>
      <Title level={4}>
        <Tooltip placement="topLeft" title="Back">
          <Button
            style={{ marginRight: "1rem" }}
            onClick={() => history.push("/autoadmin/labour_perform")}
          >
            <LeftOutlined />
          </Button>
        </Tooltip>
        Labour Perform
      </Title>

      <Form className="labour-perform-form" layout="vertical">
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item colon={false} label="Job No">
              {getFieldDecorator("JobNo", {
                rules: [{ required: true, message: "Please select Job No" }],
              })(
                <Select
                  showSearch
                  placeholder="Select Job No"
                  optionFilterProp="children"
                  disabled={fieldsLocked || isExisting}
                  onSearch={searchJobNo}
                  onChange={fetchJobDetails}
                  filterOption={false}
                  style={{ width: "100%" }}
                >
                  {jobOptions.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.jobNo}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item colon={false} label="Branch">
              {getFieldDecorator("branch")(
                <Input placeholder="Branch" disabled style={{ width: "100%" }} />
              )}
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item colon={false} label="Mechanic">
              {getFieldDecorator("mechanic", {
                rules: [{ required: true, message: "Mechanic" }],
              })(
                <Select
                  showSearch
                  showAction={["click", "focus"]}
                  filterOption={null}
                  placeholder="Mechanic"
                  disabled={fieldsLocked}
                  onSearch={searchMechanic}
                  style={{ width: "100%" }}
                >
                  {mechanics.map((obj) => (
                    <Option key={obj.id} value={obj.id}>
                      {obj.profile?.employeeName ||
                        obj.profile?.employeeId ||
                        obj.id}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item colon={false} label="Customer">
              {getFieldDecorator("customer")(
                <Input placeholder="Customer." disabled style={{ width: "100%" }} />
              )}
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item colon={false} label="Vehicle">
              {getFieldDecorator("registerNo")(
                <Input placeholder="Register No." disabled style={{ width: "100%" }} />
              )}
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item colon={false} label="Model">
              {getFieldDecorator("model")(
                <Input placeholder="Model" disabled style={{ width: "100%" }} />
              )}
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item colon={false} label="Engine No">
              {getFieldDecorator("engineNo")(
                <Input placeholder="EngineNo." disabled style={{ width: "100%" }} />
              )}
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item colon={false} label="Chassis No">
              {getFieldDecorator("chassisNo")(
                <Input placeholder="ChasisNo." disabled style={{ width: "100%" }} />
              )}
            </Form.Item>
          </Col>
        </Row>

        <LabourTable
          dataSource={labourRows}
          onChange={setLabourRows}
          editable={editable}
          vehicleMasterId={vehicleMasterId}
        />

        <Row gutter={1} style={{ justifyContent: "flex-end" }} type="flex">
          <Col span={6}>
            <Form.Item className="form-element" label="Net Amount">
              <Input
                placeholder="Net Amount"
                disabled
                value={netAmount}
              />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ float: "right" }}>
          {!editable && isExisting ? (
            modifyAccess && (
              <Button
                type="primary"
                size="large"
                onClick={() => setEditable(true)}
              >
                Modify
              </Button>
            )
          ) : (
            <>
              <Button
                style={{ marginRight: "1rem" }}
                size="large"
                onClick={() => history.push("/autoadmin/labour_perform")}
              >
                Cancel
              </Button>
              {((!isExisting && createAccess) ||
                (isExisting && modifyAccess)) && (
                <Button
                  type="primary"
                  size="large"
                  loading={saving}
                  onClick={handleSave}
                >
                  Save
                </Button>
              )}
            </>
          )}
        </div>
      </Form>
    </div>
  );
}

const LabourPerformForm = Form.create({ name: "labour_perform_form" })(
  LabourPerformFormInner
);
export default LabourPerformForm;
